import { apiGet, API_ENDPOINTS } from '../api';

/**
 * Uploads a file to S3 using a presigned URL (direct upload — bypasses API Gateway).
 * Step 1: get a presigned URL from the backend
 * Step 2: PUT the file straight to S3
 *
 * @param {File} file - The file to upload
 * @param {string} folder - Destination folder (e.g. 'blogs')
 * @returns {Promise<string>} - The public image URL to store with the blog
 */
export async function uploadFileToS3(file, folder = 'blogs') {
  const params = new URLSearchParams({
    folder,
    fileName: file.name,
    contentType: file.type || 'image/jpeg',
  });

  // Via apiGet, not a bare fetch: this is an admin call, so it must go through the
  // same session handling as the writes — refuse to send an expired JWT, and on a
  // 401 clear the session and restart the Cognito login.
  const presignedResponse = await apiGet(`${API_ENDPOINTS.S3_PRESIGNED_URL}?${params}`);

  if (!presignedResponse.ok) {
    throw new Error(`Failed to get upload URL (${presignedResponse.status})`);
  }

  const { uploadUrl, imageUrl } = await presignedResponse.json();

  // No auth header here — the signature is in the URL. Raw binary body.
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'image/jpeg' },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload image to S3 (${uploadResponse.status})`);
  }

  return imageUrl;
}
